import { defineStore } from 'pinia';
import { IToast, IToastConfig, ToastType } from '@/models/toast.model';

const createToast = (config: IToastConfig, type: ToastType): IToast => ({
  id: Math.random().toString(36).substring(2, 9),
  text: config.text,
  prefixText: config.prefixText,
  suffixText: config.suffixText,
  itemName: config.itemName,
  newItemName: config.newItemName,
  type,
});

interface IToastStore {
  toasts: IToast[];
  timeout: number; 
}

export const useToastStore = defineStore("toastStore", {
  state: (): IToastStore => ({
    toasts: [],
    timeout: 5000,
  }),
  actions: {
    addToast(config: IToastConfig, type: ToastType) {
      const toast = createToast(config, type);
      this.toasts.push(toast);

      setTimeout(() => {
        this.toasts = this.toasts.filter((t) => t.id !== toast.id);
      }, 
      config.timeout ?? this.timeout);
    },
    removeToast(id: string) {
      const toastIndex = this.toasts.findIndex(t => t.id === id);
      if (toastIndex === -1) return;
      this.toasts.splice(toastIndex, 1);
    },
    showItemSuccessfullyCreated(
      itemName: string | undefined, 
      prefixText: string | undefined = undefined,
      suffixText: string = 'was created'
    ) {
      const config = {
        prefixText,
        suffixText,
        itemName,
      } as IToastConfig;
      this.showSuccess(config);
    },
    showItemSuccessfullyUpdated(itemName: string, prefixText: string | undefined = undefined) {
      const config = {
        prefixText,
        suffixText: 'was updated',
        itemName,
      } as IToastConfig;
      this.showSuccess(config);
    },
    showItemSuccessfullyRenamed(itemName: string, newItemName: string) {
      const config = {
        suffixText: 'renamed to',
        itemName,
        newItemName,
      } as IToastConfig;
      this.showSuccess(config);
    },
    showItemSuccessfullyPublished(
      itemName: string, 
      prefixText: string | undefined = undefined
    ) {
      const config = {
        prefixText: prefixText,
        suffixText: 'was published',
        itemName: itemName,
      } as IToastConfig;
      this.showSuccess(config);
    },
    showItemSuccessfullyActivated(
      itemName: string, 
      prefixText: string | undefined = undefined
    ) {
      const config = {
        prefixText: prefixText,
        suffixText: 'was activated',
        itemName: itemName,
      } as IToastConfig;
      this.showSuccess(config);
    },
    showItemSuccessfullyDeactivated(
      itemName: string, 
      prefixText: string | undefined = undefined
    ) {
      const config = {
        prefixText: prefixText,
        suffixText: 'was deactivated',
        itemName: itemName,
      } as IToastConfig;
      this.showSuccessfullyDeleted(config);
    },
    showItemSuccessfullyLaunched(
      itemName: string, 
      prefixText: string | undefined = undefined
    ) {
      const config = {
        prefixText: prefixText,
        suffixText: 'was launched',
        itemName: itemName,
      } as IToastConfig;
      this.showSuccess(config);
    },
    showItemSuccessfullyPaused(
      itemName: string, 
      prefixText: string | undefined = undefined
    ) {
      const config = {
        prefixText: prefixText,
        suffixText: 'was paused',
        itemName: itemName,
      } as IToastConfig;
      this.showSuccessfullyDeleted(config);
    },
    showItemSuccessfullyDeleted(
      itemName: string | undefined, 
      prefixText: string | undefined = undefined
    ) {
      const config = {
        suffixText: 'was deleted',
        itemName,
        prefixText,
      } as IToastConfig;
      this.showSuccessfullyDeleted(config);
    },
    showItemSuccessfullyRemovedFromFolder(itemName: string) {
      const config = {
        suffixText: 'removed from folder',
        itemName,
      } as IToastConfig;
      this.showSuccess(config);
    },
    showItemSuccessfullyMovedToFolder(itemName: string, folderName: string) {
      const config = {
        suffixText: 'moved to',
        itemName,
        newItemName: folderName,
      } as IToastConfig;
      this.showSuccess(config);
    },
    showItemNoLongerExists(
      itemName: string | undefined, 
      prefixText: string | undefined = undefined
    ) { 
      const config = {
        suffixText: 'no longer exists',
        itemName,
        prefixText,
      } as IToastConfig;
      this.showItemNotFound(config);
    },
    showSuccess(config: IToastConfig) {
      this.addToast(config, ToastType.success);
    },
    showSuccessfullyDeleted(config: IToastConfig) {
      this.addToast(config, ToastType.successfullyDeleted);
    },
    showItemNotFound(config: IToastConfig) {
      this.addToast(config, ToastType.itemNotFound);
    },
    showError(prefixText: string) {
      const config = {
        prefixText
      } as IToastConfig;
      this.addToast(config, ToastType.error);
    },
  },
});